import React, { useEffect, useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import edit2Fill from '@iconify/icons-eva/edit-2-fill';
import trash2Fill from '@iconify/icons-eva/trash-2-fill';
import saveFill from '@iconify/icons-eva/save-fill';
import closeFill from '@iconify/icons-eva/close-fill';
import checkmarkCircle2Fill from '@iconify/icons-eva/checkmark-circle-2-fill';
import closeCircleFill from '@iconify/icons-eva/close-circle-fill';
import { FormikProvider, useFormik } from 'formik';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box,
  Button,
  Card,
  Dialog,
  Avatar,
  DialogActions,
  DialogContent,
  Table,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  Typography,
  TableContainer,
  IconButton,
  TextField,
  Tooltip,
  Checkbox,
  Alert,
  InputAdornment,
  Skeleton
} from '@material-ui/core';
import Scrollbar from 'src/components/Scrollbar';
import ConfirmationPopup from '../official-request/ConfirmationPopup';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { REST_API_END_POINT } from 'src/constants/Defaultvalues';
import {
  getLaborTimelogCost,
  getLaborTimelogBill,
  getLabourCatalogUnitCost,
  normalizeStoredLaborRate,
  shouldShowServiceOnJobCostOtherTab,
  resolveJobCostServiceLineTotal
} from 'src/utils/laborTimelogCalc';
import moment from 'moment';
import QbLogo from 'src/assets/quickbookLogo-removebg-preview.png';

const preventInvalidNumberKeys = (event) => {
  if (['-', '+', 'e', 'E'].includes(event.key)) {
    event.preventDefault();
  }
};

const toSafeNumber = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/** T&M Add Services: hide Edit when any staff timelog linked to this service has hours. */
const getTimelogLaborHoursTotal = (log) => {
  const h = toSafeNumber(log?.hours ?? log?.total_hours);
  const m = toSafeNumber(log?.minutes ?? log?.total_minutes);
  const ot = toSafeNumber(log?.ot_hours ?? log?.total_ot_hours);
  return h + m / 60 + ot;
};

const tmServiceHasLinkedLaborHours = (serviceId, staffTimelogs) => {
  if (!serviceId || !staffTimelogs?.length) return false;
  return staffTimelogs.some((log) => {
    const logServiceId = Number(log?.service_id || log?.rs_id || log?.customer_order_product_id || 0);
    return logServiceId === Number(serviceId) && getTimelogLaborHoursTotal(log) > 0;
  });
};

const round2 = (value) => Math.round((toSafeNumber(value) + Number.EPSILON) * 100) / 100;
const normalizeBillable = (value) =>
  value === true ||
  value === 1 ||
  value === '1' ||
  value === 'true';

const calculateMargin = (totalBill, totalCost) => {
  const safeTotalBill = toSafeNumber(totalBill);
  const safeTotalCost = toSafeNumber(totalCost);
  if (!safeTotalBill) return safeTotalCost > 0 ? -100 : 0;
  return round2(((safeTotalBill - safeTotalCost) / safeTotalCost) * 100);
};

const calculateRowValues = ({ qty, unitCost, billRate, billable }) => {
  const safeQty = toSafeNumber(qty);
  const safeUnitCost = toSafeNumber(unitCost);
  const safeBillRate = toSafeNumber(billRate);
  const safeBillable = normalizeBillable(billable);
  const cost = safeQty * safeUnitCost;
  const bill = safeBillable ? safeQty * safeBillRate : 0;

  return {
    qty: round2(safeQty),
    unitCost: round2(safeUnitCost),
    billRate: round2(safeBillRate),
    billable: safeBillable,
    cost: round2(cost),
    bill: round2(bill)
  };
};

const getExpectedQty = (service) => toSafeNumber(service?.qty);
const getActualQty = (service) => toSafeNumber(service?.jobcost_quantity);
const getEffectiveQty = (service) => {
  return toSafeNumber(service?.jobcost_quantity);
};

const getEffectiveUnitCost = (service, qty, isTM = false) => {
  // 1. Priority: Direct Override Unit Cost
  const directJobCostUnit = service?.jobcost_unit_cost;
  if (directJobCostUnit != null && directJobCostUnit !== '' && toSafeNumber(directJobCostUnit) !== 0) {
    // OLD: return toSafeNumber(directJobCostUnit);
    const jc = toSafeNumber(directJobCostUnit);
    const normalized = normalizeStoredLaborRate(service, isTM, jc);
    if (normalized > 0) return normalized;
  }

  // 2. Priority: Saved Job Cost Total (even if 0)
  const totalCost = service?.jobcost_total_cost;
  if (totalCost != null && totalCost !== '' && toSafeNumber(totalCost) !== 0) {
    const safeQty = toSafeNumber(qty);
    return safeQty > 0 ? toSafeNumber(totalCost) / safeQty : 0;
  }

  // 3. Fallback: Original / Default Rates
  // 3. Fallback: catalog cost (T&M = total_cost with markup, else cost without markup)
  const catalogCost = getLabourCatalogUnitCost(service, isTM);
  if (catalogCost > 0) return catalogCost;

  // OLD: // 3. Fallback: Original / Default Rates — originalCost only (no T&M markup).
  const originalCost = service?.originalCost;
  if (originalCost != null && originalCost !== '') return toSafeNumber(originalCost);
  const unitCost = service?.unit_cost;
  if (unitCost != null && unitCost !== '') {
    const normalized = normalizeStoredLaborRate(service, isTM, toSafeNumber(unitCost));
    if (normalized > 0) return normalized;
  }
  const cost = service?.cost;
  if (cost != null && cost !== '') return toSafeNumber(cost);

  return 0;
};

const getEffectiveBillRate = (service, qty) => {
  // 1. Priority: Direct Override Bill Unit Price
  const billUnit = service?.bill_unit_price;
  if (billUnit != null && billUnit !== '') return toSafeNumber(billUnit);
  const legacyBillRate = service?.jobcost_bill_rate;
  if (legacyBillRate != null && legacyBillRate !== '') return toSafeNumber(legacyBillRate);

  // 2. Priority: Saved Job Cost Total Bill (even if 0)
  const totalBill = service?.total_bill ?? service?.jobcost_total_bill;
  if (totalBill != null && totalBill !== '') {
    const safeQty = toSafeNumber(qty);
    return safeQty > 0 ? toSafeNumber(totalBill) / safeQty : 0;
  }

  // 3. Fallback: Original / Default Rates
  const billRate = service?.bill_rate;
  if (billRate != null && billRate !== '') return toSafeNumber(billRate);

  return 0;
};

const getHoursTooltip = (rawService, qty, breakdown) => {
  const expected = getExpectedQty(rawService);
  const actual = getActualQty(rawService);
  const effective = toSafeNumber(qty);
  let text = `Hours (display) = Actual Qty if > 0, else Expected Qty\nActual Qty: ${actual.toFixed(2)}\nExpected Qty: ${expected.toFixed(2)}\nDisplayed Hours: ${effective.toFixed(2)}`;
  if (breakdown && breakdown.ot > 0) {
    const eq = breakdown.reg + (breakdown.ot * 1.5);
    text += `\nNew Calculation Breakdown: ${breakdown.reg.toFixed(2)} Reg + (${breakdown.ot.toFixed(2)} OT x 1.5) = ${eq.toFixed(2)} Equivalent Hours`;
  }
  return text;
};

const getCostRateTooltip = (unitCost) =>
  `Cost Rate = $${toSafeNumber(unitCost).toFixed(2)} (service cost rate used for cost calculation)`;

const getBillRateTooltip = (billRate) =>
  `Bill Rate = $${toSafeNumber(billRate).toFixed(2)} per hour (used for bill when Billable is on)`;

const getCostTooltip = (qty, unitCost, cost, breakdown) => {
  const q = toSafeNumber(qty);
  const uc = toSafeNumber(unitCost);
  const c = toSafeNumber(cost);
  
  if (breakdown && breakdown.ot > 0) {
    const regAmt = breakdown.reg * uc;
    const otAmt = breakdown.ot * uc * 1.5;
    return `Cost Calculation (1.5x OT):\nRegular: ${breakdown.reg.toFixed(2)} x $${uc.toFixed(2)} = $${regAmt.toFixed(2)}\nOT: ${breakdown.ot.toFixed(2)} x $${uc.toFixed(2)} x 1.5 = $${otAmt.toFixed(2)}\nTotal Cost: $${c.toFixed(2)}`;
  }

  if (Math.abs(c - (q * uc)) > 0.01) {
    return `Cost: $${c.toFixed(2)} (Calculated from clock-in data including Regular and OT at 1.5x)`;
  }
  return `Cost = Hours x Cost Rate = ${q.toFixed(2)} x $${uc.toFixed(2)} = $${c.toFixed(2)}`;
};

const getBillTooltip = (qty, billRate, billable, bill, breakdown) => {
  if (!normalizeBillable(billable)) return `Bill = 0 (Billable is false)`;
  const q = toSafeNumber(qty);
  const br = toSafeNumber(billRate);
  const b = toSafeNumber(bill);

  if (breakdown && breakdown.ot > 0) {
    const regBill = breakdown.reg * br;
    const otBill = breakdown.ot * br * 1.5;
    return `Bill Calculation (1.5x OT):\nRegular: ${breakdown.reg.toFixed(2)} x $${br.toFixed(2)} = $${regBill.toFixed(2)}\nOT: ${breakdown.ot.toFixed(2)} x $${br.toFixed(2)} x 1.5 = $${otBill.toFixed(2)}\nTotal Bill: $${b.toFixed(2)}`;
  }

  if (Math.abs(b - (q * br)) > 0.01) {
    return `Bill: $${b.toFixed(2)} (Calculated from clock-in data including Regular and OT at 1.5x)`;
  }
  return `Bill = Hours x Bill Rate = ${q.toFixed(2)} x $${br.toFixed(2)} = $${b.toFixed(2)}`;
};

const getBillableTooltip = (billable) =>
  normalizeBillable(billable)
    ? 'Billable is true, so Bill uses Hours x Bill Rate'
    : 'Billable is false, so Bill is 0';

export default function JobCostEquipmentsTab({
  services = [],
  setAllServices,
  theme,
  isLoading = false,
  onAddEquipmentEntry,
  onSummaryChange,
  onServicesRefresh,
  userId,
  requestId,
  projectId,
  projectData,
  manuallyAddedPayouts = [],
  totalManualHours = 0,
  staffTimelogs = [],
  isTM = false,
  onRowsCountChange
}) {

  const tableHeaders = [
    { id: 'date', label: 'Date', align: 'left' },
    { id: 'item', label: 'Name / Description', align: 'left' },
    ...(!isTM ? [{ id: 'expt_qty', label: 'Expt Qty', align: 'center' }] : []),
    { id: 'qty', label: 'Qty', align: 'center' },
    { id: 'unitCost', label: 'Unit Cost', align: 'left' },
    /* ...(!isTM ? [
      { id: 'billRate', label: 'Unit Bill', align: 'left' }
    ] : []), */
    { id: 'cost', label: 'Cost', align: 'left' },
    /* ...(!isTM ? [
      { id: 'bill', label: 'Bill', align: 'left' },
      { id: 'billable', label: 'Billable', align: 'center' }
    ] : []), */
    { id: 'actions', label: 'Actions', align: 'center' }
  ];

  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState([]);
  const [editingUid, setEditingUid] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [editServiceConfirmation, setEditServiceConfirmation] = useState({
    open: false,
    row: null,
    isProductionLabor: false
  });

  const getServiceLaborCost = React.useCallback((serviceId) => {
    if (!serviceId || !staffTimelogs || staffTimelogs.length === 0) return null;
    const matches = staffTimelogs.filter(log => {
      const logServiceId = Number(log?.service_id || log?.rs_id || log?.customer_order_product_id || 0);
      return logServiceId === Number(serviceId);
    });
    if (matches.length === 0) return null;
    return matches.reduce((sum, log) => sum + getLaborTimelogCost(log), 0);
  }, [staffTimelogs]);

  const getServiceLaborBreakdown = React.useCallback((serviceId) => {
    if (!serviceId || !staffTimelogs || staffTimelogs.length === 0) return null;
    const logs = staffTimelogs.filter(log => {
      const logServiceId = Number(log?.service_id || log?.rs_id || log?.customer_order_product_id || 0);
      return logServiceId === Number(serviceId);
    });
    if (logs.length === 0) return null;
    let reg = 0;
    let ot = 0;
    logs.forEach(log => {
      const h = Number(log.hours || log.total_hours || 0);
      const m = Number(log.minutes || log.total_minutes || 0);
      const otH = Number(log.ot_hours || log.total_ot_hours || 0);
      const total = h + (m / 60);
      reg += Math.max(0, total - otH);
      ot += otH;
    });
    return { reg, ot };
  }, [staffTimelogs]);

  const getServiceLaborRate = React.useCallback((serviceId) => {
    if (!serviceId) return null;
    const svc = (services || []).find((s) => Number(s.id) === Number(serviceId));
    if (svc) {
      const qty = getEffectiveQty(svc);
      const lineRate = getEffectiveUnitCost(svc, qty, isTM);
      if (lineRate > 0) return lineRate;
      return null;
    }
    if (!staffTimelogs || staffTimelogs.length === 0) return null;
    const logs = staffTimelogs.filter(log => {
      const logServiceId = Number(log?.service_id || log?.rs_id || log?.customer_order_product_id || 0);
      return logServiceId === Number(serviceId);
    });

    // OLD (only when no `services` row matched): summed unique employee hourly_rate from timelogs.
    const employeeRates = new Map();
    const activeLogs = logs.filter(l => (Number(l.hours || l.total_hours || 0) + Number(l.ot_hours || l.total_ot_hours || 0) + Number(l.minutes || l.total_minutes || 0) / 60) > 0);

    if (activeLogs.length === 0) return null;

    activeLogs.forEach(l => {
      const empId = l.staff_id || l.employee_id || l.userId || l.employee_name || `unknown-${Math.random()}`;
      const rate = Number(l.hourly_rate ?? l.costRate ?? 0);
      if (!employeeRates.has(empId)) {
        employeeRates.set(empId, rate);
      }
    });

    let totalRate = 0;
    employeeRates.forEach(rate => { totalRate += rate; });

    return totalRate > 0 ? totalRate : null;
  }, [staffTimelogs, services, isTM]);

  const getServiceLaborBill = React.useCallback((serviceId) => {
    if (!serviceId || !staffTimelogs || staffTimelogs.length === 0) return null;
    const matches = staffTimelogs.filter(log => {
      const logServiceId = Number(log?.service_id || log?.rs_id || log?.customer_order_product_id || 0);
      return logServiceId === Number(serviceId);
    });
    if (matches.length === 0) return null;
    return matches.reduce((sum, log) => sum + getLaborTimelogBill(log), 0);
  }, [staffTimelogs]);

  const getServiceLaborBillRate = React.useCallback((serviceId) => {
    if (!serviceId || !staffTimelogs || staffTimelogs.length === 0) return null;
    const logs = staffTimelogs.filter(log => {
      const logServiceId = Number(log?.service_id || log?.rs_id || log?.customer_order_product_id || 0);
      return logServiceId === Number(serviceId);
    });
    
    const employeeRates = new Map();
    const activeLogs = logs.filter(l => (Number(l.hours || l.total_hours || 0) + Number(l.ot_hours || l.total_ot_hours || 0) + Number(l.minutes || l.total_minutes || 0) / 60) > 0);
    
    if (activeLogs.length === 0) return null;

    activeLogs.forEach(l => {
      const empId = l.staff_id || l.employee_id || l.userId || l.employee_name || `unknown-${Math.random()}`;
      
      const isBillableLog =
        l.billable === 1 ||
        l.billable === '1' ||
        l.billable === true ||
        l.is_billable === 1 ||
        l.is_billable === '1' ||
        l.is_billable === true;
      
      const rate = isBillableLog ? Number(l.bill_rate ?? 0) : 0;
      
      if (!employeeRates.has(empId) || (isBillableLog && employeeRates.get(empId) === 0)) {
        employeeRates.set(empId, rate);
      }
    });
    
    let totalRate = 0;
    employeeRates.forEach(rate => { totalRate += rate; });
    
    return totalRate > 0 ? totalRate : null;
  }, [staffTimelogs]);

  const mappedRows = useMemo(
    () =>
      (services || []).map((service, index) => {
  // const mappedRows = useMemo(() => {
  //   // Identify services missing from 'services' array but present in 'staffTimelogs'
  //   const existingServiceIds = new Set((services || []).map((s) => Number(s?.id)));
  //   const missingServicesFromLogs = [];
  //   (staffTimelogs || []).forEach((log) => {
  //     const sid = Number(log?.service_id || log?.rs_id || log?.customer_order_product_id || 0);
  //     const logHours = Number(log?.hours || 0);
  //     const logCost = Number(log?.total_cost || 0);
      
  //     if (sid > 0 && (logHours > 0 || logCost > 0) && !existingServiceIds.has(sid) && !missingServicesFromLogs.some((ms) => Number(ms.id) === sid)) {
  //       missingServicesFromLogs.push({
  //         id: sid,
  //         name: log.service_name || (sid === 10 ? 'INSTALL LABOR' : `Service ${sid}`),
  //         service_label: log.service_name || (sid === 10 ? 'INSTALL LABOR' : `Service ${sid}`),
  //         jobcost_quantity: 0,
  //         qty: 0,
  //         taken: 0,
  //         is_virtual: true
  //       });
  //     }
  //   });

  //   const allServices = [...(services || []), ...missingServicesFromLogs]; // Old codecommented because of unknown service came if any issue come from this fix it and uncomment this code

  //   return allServices.map((service, index) => {
        const serviceQty = getEffectiveQty(service);
        const laborCost = getServiceLaborCost(service?.id);
        const laborBill = getServiceLaborBill(service?.id);
        const laborRate = getServiceLaborRate(service?.id);
        const laborBillRate = getServiceLaborBillRate(service?.id);

        const effectiveUnitCostDef = getEffectiveUnitCost(service, serviceQty, isTM);
        const unitCost = (laborRate !== null) ? laborRate : ((Number(service?.labour_cost_service_id) > 0 && toSafeNumber(service?.originalCost) > 0) ? toSafeNumber(service.originalCost) : effectiveUnitCostDef);
        
        const billRateRaw = laborBillRate !== null ? laborBillRate : getEffectiveBillRate(service, serviceQty);
        const calculated = calculateRowValues({
          qty: serviceQty,
          unitCost,
          billRate: billRateRaw,
          billable: normalizeBillable(service?.billable ?? service?.is_billable ?? true)
        });

        const hasManualUsage = toSafeNumber(service?.jobcost_quantity) > 0;
        const isManualZero = service?.jobcost_quantity != null && service?.jobcost_quantity !== '' && toSafeNumber(service?.jobcost_quantity) === 0;

        // if (calculated.qty === 0 && laborCost === null) {
          if (calculated.qty === 0) {
          calculated.cost = 0;
          calculated.bill = 0;
        } else if (hasManualUsage) {
          const resolvedLineCost = resolveJobCostServiceLineTotal(service, isTM, serviceQty);
          // OLD: calculated.cost = round2(service?.jobcost_total_cost || calculated.cost);
          calculated.cost = round2(resolvedLineCost > 0 ? resolvedLineCost : (service?.jobcost_total_cost || calculated.cost));
          calculated.bill = calculated.billable ? round2(service?.jobcost_total_bill || service?.total_bill || calculated.bill) : 0;
        } else if (laborCost !== null) {
          calculated.cost = round2(laborCost);
          if (laborBill !== null) {
            calculated.bill = calculated.billable ? round2(laborBill) : 0;
          }
        } else if (service?.jobcost_total_cost != null && service?.jobcost_total_cost !== '') {
           calculated.cost = round2(service.jobcost_total_cost);
        }

        const showLabor = laborRate !== null && !hasManualUsage && !isManualZero;

        return {
          uid: `service-${index}-${service?.id || index}`,
          serviceId: service?.id,
          sourceIndex: index,
          raw: service,
          laborBreakdown: showLabor ? getServiceLaborBreakdown(service?.id) : null,
          date: service?.jobcost_timestamp ? moment.unix(service.jobcost_timestamp).format('DD/MM/YYYY') : (service?.date || service?.created_at || "-"),
          item:
            service?.name ||
            service?.service_name ||
            (service?.service_label != null && String(service.service_label).trim() !== ''
              ? String(service.service_label).trim()
              : null) ||
            '—',
          exptQty: getExpectedQty(service),
          qty: calculated.qty,
          unitCost: calculated.unitCost,
          billRate: calculated.billRate,
          cost: calculated.cost,
          bill: calculated.bill,
          billable: calculated.billable
        };
//     });
//   },
//   [services, staffTimelogs, getServiceLaborCost, getServiceLaborBill, getServiceLaborRate, getServiceLaborBillRate, getServiceLaborBreakdown] // Old codecommented because of unknown service came if any issue come from this fix it and uncomment this code
// );
  }),
  [services, staffTimelogs, getServiceLaborCost, getServiceLaborBill, getServiceLaborRate, getServiceLaborBillRate, getServiceLaborBreakdown]
);

  useEffect(() => {
    // Only show services in this "Other" tab that are NOT supposed to be in the "Labor" tab.
    // OLD: inline filter — non-T&M allowed Install Labor when from_job === 1 (showed INSTALL LABOR on Other tab).
    // const otherTabRows = mappedRows.filter(row => {
    //   const s = row.raw;
    //   const isInstallLabor = Number(row.serviceId) === 10 || Number(s?.labour_cost_service_id) === 10;
    //   if (isTM) {
    //     // T&M: all except Install Labor
    //     return !isInstallLabor;
    //   } else {
    //     // Non-T&M: only those added FROM job cost
    //     return Number(s.from_job) === 1;
    //   }
    // });
    const otherTabRows = mappedRows.filter((row) =>
      shouldShowServiceOnJobCostOtherTab(row.raw, isTM)
    );
    setRows(otherTabRows);
  }, [mappedRows, isTM]);

  useEffect(() => {
    if (onRowsCountChange) {
      onRowsCountChange(rows.length);
    }
  }, [rows, onRowsCountChange]);

  const summaryStats = useMemo(() => {
    const totalCost = rows.reduce((sum, row) => sum + (Number(row.cost) || 0), 0);
    const totalBillable = rows.reduce((sum, row) => sum + (row.billable ? Number(row.bill) || 0 : 0), 0);
    return { totalCost, totalBillable, marginPercent: calculateMargin(totalBillable, totalCost) };
  }, [rows]);

  useEffect(() => {
    if (onSummaryChange) {
      onSummaryChange({
        cost: Number(summaryStats.totalCost || 0),
        billable: Number(summaryStats.totalBillable || 0),
        marginPercent: Number(summaryStats.marginPercent || 0)
      });
    }
  }, [summaryStats, onSummaryChange]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: { item: '', qty: 0, unitCost: 0, billRate: 0, billable: true },
    onSubmit: async (values) => {
      if (!editingUid) return;
      const row = rows.find((r) => r.uid === editingUid);
      if (!row) return;
      if (!requestId || !projectId || !userId || !row.serviceId) {
        enqueueSnackbar('Missing request, project, user, or service id', { variant: 'error' });
        return;
      }

      const expectedQty = getExpectedQty(row.raw);
      const inputQty = toSafeNumber(values.qty);
      const taken = 1;
      const calculated = calculateRowValues({
        qty: inputQty,
        unitCost: values.unitCost,
        billRate: values.billRate,
        billable: values.billable
      });

      setSaving(true);
      try {
        const res = await axios.post(
          `${REST_API_END_POINT}project/save-job-cost-equipment-service`,
          {
            serviceId: row.serviceId,
            requestId,
            projectId,
            userId,
            serviceName: values.item,
            quantity: calculated.qty,
            unitCost: calculated.unitCost,
            billRate: calculated.billRate,
            billable: calculated.billable,
            jobcost_timestamp: Math.floor(Date.now() / 1000)
          },
          { headers: { 'Content-Type': 'application/json' } }
        );

        if (res?.data?.status !== 1) {
          throw new Error(res?.data?.message || 'Save failed');
        }

        if (row?.serviceId && projectId && userId) {
          try {
            await axios.post(`${REST_API_END_POINT}project/delete-staff-task-timelogs-by-service`, {
              serviceId: row.serviceId,
              projectId,
              userId,
              isProductionLabor: Boolean(row?.raw?.__isRecipeGroup)
            });
          } catch (err) {
            enqueueSnackbar('Failed to clear existing clock-in data for this service', { variant: 'warning' });
          }
        }

        if (onServicesRefresh) {
          onServicesRefresh();
        } else if (Array.isArray(res.data.services)) {
          setAllServices(res.data.services);
        } else {
          setAllServices((prev) =>
            prev.map((service) =>
              Number(service?.id) === Number(row.serviceId)
                ? {
                    ...service,
                    name: values.item,
                    service_label: values.item,
                    bill_unit_price: calculated.billRate,
                    jobcost_quantity: calculated.qty,
                    jobcost_unit_cost: calculated.unitCost,
                    jobcost_total_cost: calculated.cost,
                    jobcost_total_bill: calculated.bill,
                    total_bill: calculated.bill,
                    billable: calculated.billable ? 1 : 0,
                    jobcost_timestamp: Math.floor(Date.now() / 1000),
                    taken
                  }
                : service
            )
          );
        }

        enqueueSnackbar(res.data.message || 'Service saved', { variant: 'success' });
        setEditingUid(null);
      } catch (err) {
        enqueueSnackbar(err?.message || 'Failed to save service', { variant: 'error' });
      } finally {
        setSaving(false);
      }
    }
  });

  const updateFormikCalculatedValues = (nextValues) => {
    const calculated = calculateRowValues({
      qty: nextValues.qty,
      unitCost: nextValues.unitCost,
      billRate: nextValues.billRate,
      billable: nextValues.billable
    });
    formik.setValues({
      ...nextValues,
      qty: calculated.qty,
      unitCost: calculated.unitCost,
      billRate: calculated.billRate,
      billable: calculated.billable
    });
  };

  const handleEditRow = (row) => {
    setEditServiceConfirmation({
      open: true,
      row,
      isProductionLabor: Boolean(row?.raw?.__isRecipeGroup)
    });
  };

  const handleEditServiceConfirm = () => {
    const row = editServiceConfirmation?.row;
    if (!row) return;
    setEditingUid(row.uid);
    formik.setValues({
      item: row.item,
      qty: round2(row.qty),
      unitCost: isTM
        ? row.unitCost
        : (Number(row.raw?.labour_cost_service_id) > 0 && toSafeNumber(row.raw?.originalCost) > 0)
          ? row.raw.originalCost
          : row.unitCost,
      billRate: row.billRate,
      billable: row.billable
    });
    setEditServiceConfirmation({ open: false, row: null, isProductionLabor: false });
  };

  const handleEditServiceCancel = () => {
    setEditServiceConfirmation({ open: false, row: null, isProductionLabor: false });
  };

  const handleDeleteConfirm = () => {
    if (!itemToDelete) return;
    const serviceId = itemToDelete?.serviceId;
    if (!serviceId || !requestId || !projectId || !userId) {
      setAllServices((prev) =>
        prev.filter((service, index) =>
          itemToDelete?.serviceId
            ? Number(service?.id) !== Number(itemToDelete.serviceId)
            : index !== itemToDelete.sourceIndex
        )
      );
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      return;
    }

    const from_job = Number(projectData?.project_category) === 12 ? 1 : (itemToDelete?.raw?.from_job || 0);
      axios
        .post(`${REST_API_END_POINT}request/delete-request-service`, {
          serviceId,
          userId,
          from_job,
          is_recipe_group: Boolean(itemToDelete?.raw?.__isRecipeGroup),
          request_id: requestId,
          projectId,
          orderId: requestId
        })
        .then((response) => {
          if (response?.data?.status === 1) {
            if (onServicesRefresh) {
              onServicesRefresh();
            } else {
              setAllServices(response?.data?.services || []);
            }
            enqueueSnackbar(response?.data?.message || 'Service deleted successfully', { variant: 'success' });
          } else {
            enqueueSnackbar(response?.data?.message || 'Failed to delete service', { variant: 'error' });
          }
        })
        .catch(() => {
          enqueueSnackbar('Error deleting service', { variant: 'error' });
        })
        .finally(() => {
          setDeleteDialogOpen(false);
          setItemToDelete(null);
        });
    };

  return (
    <Box sx={{ mt: 3, pl: 2, pr: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }} alignItems={{ xs: 'flex-start', md: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'transparent', px: 2, py: 1, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
          <Box component={'span'} className='mdi--dollar' width={18} height={18} sx={{ mr: 0.5, color: 'text.secondary' }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary' }}>
            Cost: ${summaryStats.totalCost.toFixed(2)}
          </Typography>
        </Box>
        {/* {!isTM && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'primary.main', px: 2, py: 1, borderRadius: 1 }}>
              <Box component={'span'} className='mdi--dollar' width={18} height={18} sx={{ mr: 0.5, color: 'common.white' }} />
              <Typography variant="subtitle2" sx={{ color: 'common.white', fontWeight: 800 }}>
                Bill: ${summaryStats.totalBillable.toFixed(2)}
              </Typography>
            </Box>
            <Tooltip
              title={`Margin % = ((Billable - Cost) / Cost) x 100 = (($${summaryStats.totalBillable.toFixed(2)} - $${summaryStats.totalCost.toFixed(2)}) / $${summaryStats.totalCost.toFixed(2)}) x 100 = ${summaryStats.marginPercent.toFixed(2)}%`}
              arrow
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: (t) =>
                    summaryStats.marginPercent < 0
                      ? (t.palette.mode === 'dark' ? `${t.palette.error.main}1a` : '#ffebee')
                      : (t.palette.mode === 'dark' ? `${t.palette.success.main}1a` : '#e8f5e9'),
                  px: 2,
                  py: 1,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: summaryStats.marginPercent < 0 ? 'error.light' : 'success.light'
                }}
              >
                <Box
                  component={'span'}
                  className={summaryStats.marginPercent < 0 ? 'ph--trend-down' : 'ph--trend-up'}
                  width={18}
                  height={18}
                  sx={{ mr: 1, color: summaryStats.marginPercent < 0 ? 'error.main' : '#54D62C' }}
                />
                <Typography variant="subtitle2" sx={{ color: summaryStats.marginPercent < 0 ? 'error.dark' : 'success.dark', fontWeight: 800 }}>
                  Margin: {summaryStats.marginPercent.toFixed(2)}%
                </Typography>
              </Box>
            </Tooltip>
          </>
        )} */}
      </Stack>

      <Card sx={{ boxShadow: 'none', border: `1px solid ${theme.palette.divider}`, borderRadius: 1.5, overflow: 'hidden' }}>
        <Scrollbar>
          <TableContainer sx={{ minWidth: 900 }}>
            <Table size="small">
              <TableBody>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  {tableHeaders.map((head) => (
                    <TableCell key={head.id} align={head.align} sx={{ fontWeight: 800, color: 'text.secondary', py: 1.8, fontSize: '0.8rem', textTransform: 'uppercase' }}>
                      {head.label}
                    </TableCell>
                  ))}
                </TableRow>

                <AnimatePresence>
                  {isLoading ? (
                    [...Array(4)].map((_, i) => (
                      <TableRow key={i}>
                        {[...Array(tableHeaders.length)].map((__, j) => (
                          <TableCell key={j}>
                            <Skeleton variant="text" width="60%" />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                  // OLD: rows.filter(row => !(isTM && (Number(row.serviceId) === 10 || Number(row.raw?.labour_cost_service_id) === 10))).map(...)
                  rows.map((row, index) => {
                    const isEditing = editingUid === row.uid;
                    const fv = formik.values;
                    const editCalculated = isEditing
                      ? calculateRowValues({
                          qty: fv.qty,
                          unitCost: fv.unitCost,
                          billRate: fv.billRate,
                          billable: fv.billable
                        })
                      : null;
                    return (
                      <TableRow
                        key={row.uid}
                        component={motion.tr}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        sx={{ borderBottom: (t) => `solid 1px ${t.palette.divider}`, '&:hover': { bgcolor: 'action.hover' } }}
                      >
                        <TableCell>{row.date}</TableCell>
                        {isEditing ? (
                          <FormikProvider value={formik}>
                            <>
                              <TableCell>
                                {isTM ? (
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    {fv.item}
                                  </Typography>
                                ) : (
                                  <TextField size="small" fullWidth value={fv.item} onChange={(e) => formik.setFieldValue('item', e.target.value)} />
                                )}
                              </TableCell>
                              {!isTM && (
                                <TableCell align="center">
                                  <Typography variant="body2">{row.exptQty}</Typography>
                                </TableCell>
                              )}
                              <TableCell align="center">
                                <TextField
                                  size="small"
                                  type="number"
                                  value={fv.qty}
                                  onKeyDown={preventInvalidNumberKeys}
                                  inputProps={{ min: 0, step: 1 }}
                                  onChange={(e) => {
                                    const next = { ...fv, qty: e.target.value };
                                    updateFormikCalculatedValues(next);
                                  }}
                                  sx={{ width: 80, mx: 'auto' }}
                                />
                              </TableCell>
                              {isTM ? (
                                <TableCell>
                                  <Typography variant="body2">
                                    ${Number(fv.unitCost || 0).toFixed(2)}
                                  </Typography>
                                </TableCell>
                              ) : (
                                <TableCell>
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={fv.unitCost}
                                    onKeyDown={preventInvalidNumberKeys}
                                    inputProps={{ min: 0 }}
                                    onChange={(e) => {
                                      const next = { ...fv, unitCost: e.target.value };
                                      updateFormikCalculatedValues(next);
                                    }}
                                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                    sx={{ width: 110 }}
                                  />
                                </TableCell>
                              )}
                              {/* {!isTM && (
                                <TableCell>
                                  <Tooltip title={getBillRateTooltip(editCalculated.billRate)} arrow>
                                    <TextField
                                      size="small"
                                      type="number"
                                      value={fv.billRate}
                                      onKeyDown={preventInvalidNumberKeys}
                                      inputProps={{ min: 0 }}
                                      onChange={(e) => {
                                        const next = { ...fv, billRate: e.target.value };
                                        updateFormikCalculatedValues(next);
                                      }}
                                      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                      sx={{ width: 110 }}
                                    />
                                  </Tooltip>
                                </TableCell>
                              )} */}
                              <TableCell>
                                <Tooltip title={getCostTooltip(fv.qty, fv.unitCost, editCalculated.cost, row.laborBreakdown)} arrow>
                                  <span>${editCalculated.cost.toFixed(2)}</span>
                                </Tooltip>
                              </TableCell>
                              {/* {!isTM && (
                                <>
                                  <TableCell>
                                    <Tooltip title={getBillTooltip(fv.qty, editCalculated.billRate, fv.billable, editCalculated.bill, row.laborBreakdown)} arrow>
                                      <span>${editCalculated.bill.toFixed(2)}</span>
                                    </Tooltip>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Tooltip title={getBillableTooltip(fv.billable)} arrow>
                                      <Checkbox
                                        checked={Boolean(fv.billable)}
                                        onChange={(e) => {
                                          const next = { ...fv, billable: e.target.checked };
                                          updateFormikCalculatedValues(next);
                                        }}
                                      />
                                    </Tooltip>
                                  </TableCell>
                                </>
                              )} */}
                              <TableCell align="center">
                                <Stack direction="row" spacing={1} justifyContent="center">
                                  <IconButton size="small" color="primary" disabled={saving} onClick={formik.handleSubmit}>
                                    <Icon icon={saveFill} width={18} height={18} />
                                  </IconButton>
                                  <IconButton size="small" color="inherit" disabled={saving} onClick={() => setEditingUid(null)}>
                                    <Icon icon={closeFill} width={18} height={18} />
                                  </IconButton>
                                </Stack>
                              </TableCell>
                            </>
                          </FormikProvider>
                        ) : (
                          <>
                            <TableCell>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                {row.raw?.finalTaken === 1 && projectData?.syncWithQuickbooks === 1 && (
                                  <Avatar
                                    src={QbLogo}
                                    alt="QB"
                                    sx={{ width: 24, height: 24, objectFit: 'cover' }}
                                  />
                                )}
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    fontWeight: 600,
                                    maxWidth: 180,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {row.item}
                                </Typography>
                              </Stack>
                            </TableCell>
                            {!isTM && (
                              <TableCell align="center">
                                <Typography variant="body2">{row.exptQty}</Typography>
                              </TableCell>
                            )}
                            <TableCell align="center">
                              <Tooltip title={getHoursTooltip(row.raw, row.qty, row.laborBreakdown)} arrow>
                                <span>{Number(row.qty || 0).toFixed(2)}</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <Tooltip title={getCostRateTooltip(row.unitCost)} arrow>
                                <span>${Number(row.unitCost || 0).toFixed(2)}</span>
                              </Tooltip>
                            </TableCell>
                            {/* {!isTM && (
                              <TableCell>
                                <Tooltip title={getBillRateTooltip(row.billRate)} arrow>
                                  <span>${Number(row.billRate || 0).toFixed(2)}</span>
                                </Tooltip>
                              </TableCell>
                            )} */}
                            <TableCell>
                              <Tooltip title={getCostTooltip(row.qty, row.unitCost, row.cost, row.laborBreakdown)} arrow>
                                <span>${Number(row.cost || 0).toFixed(2)}</span>
                              </Tooltip>
                            </TableCell>
                            {/* {!isTM && (
                              <>
                                <TableCell sx={{ fontWeight: 900, color: 'primary.main' }}>
                                  <Tooltip title={getBillTooltip(row.qty, row.billRate, row.billable, row.bill, row.laborBreakdown)} arrow>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 900, color: 'primary.main' }}>${Number(row.bill || 0).toFixed(2)}</Typography>
                                  </Tooltip>
                                </TableCell>
                                <TableCell align="center">
                                  <Tooltip title={getBillableTooltip(row.billable)} arrow>
                                    <Box sx={{ width: 20, height: 20, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: row.billable ? 'success.main' : 'error.main', mx: 'auto' }}>
                                      <Icon icon={row.billable ? checkmarkCircle2Fill : closeCircleFill} width={14} height={14} style={{ color: 'white' }} />
                                    </Box>
                                  </Tooltip>
                                </TableCell>
                              </>
                            )} */}
                            <TableCell align="center">
                              <Stack direction="row" spacing={1} justifyContent="center">
                                {/* OLD: always show Edit + Delete for every service row
                                <IconButton size="small" onClick={() => handleEditRow(row)}><Icon icon={edit2Fill} width={18} height={18} /></IconButton>
                                <IconButton size="small" color="error" onClick={() => { setItemToDelete(row); setDeleteDialogOpen(true); }}><Icon icon={trash2Fill} width={18} height={18} /></IconButton>
                                */}
                                {(!isTM || !tmServiceHasLinkedLaborHours(row.serviceId, staffTimelogs)) && (
                                  <IconButton size="small" onClick={() => handleEditRow(row)}>
                                    <Icon icon={edit2Fill} width={18} height={18} />
                                  </IconButton>
                                )}
                                <IconButton size="small" color="error" onClick={() => { setItemToDelete(row); setDeleteDialogOpen(true); }}>
                                  <Icon icon={trash2Fill} width={18} height={18} />
                                </IconButton>
                              </Stack>
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    );
                  })
                  )}

                  {/* OLD: {!isLoading && rows.filter(row => !(isTM && (Number(row.serviceId) === 10 || Number(row.raw?.labour_cost_service_id) === 10))).length === 0 && ( */}
                  {!isLoading && rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={tableHeaders.length} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">No service entries</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </AnimatePresence>
              </TableBody>
            </Table>
          </TableContainer>
        </Scrollbar>
      </Card>
      <Button
        variant="text"
        onClick={() => {
          if (onAddEquipmentEntry) onAddEquipmentEntry();
        }}
        startIcon={<Box component="span" sx={{ fontSize: 20, lineHeight: 1, fontWeight: 500 }}>+</Box>}
        sx={{ mt: 2, mb: 2, pl: 1, color: 'primary.main', fontWeight: 700 }}
      >
        Add Other Entry
      </Button>

      <ConfirmationPopup
        open={deleteDialogOpen}
        handleClose={() => setDeleteDialogOpen(false)}
        message="Are you sure you want to remove this service entry?"
        handleClick={handleDeleteConfirm}
      />

      <Dialog open={editServiceConfirmation.open} onClose={handleEditServiceCancel}>
        <DialogContent>
          <Alert severity="warning">
            When we edit the hours, any already clocked-in data (if present) will be cleared and replaced with the service hourly cost. If no clock-in data exists, only the service hourly cost will be applied. Are you sure?
          </Alert>
          {editServiceConfirmation.isProductionLabor && (manuallyAddedPayouts?.length > 0) && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Note: This is Production Labor with manually added task clock-in data. The manual hours ({Number(totalManualHours || 0).toFixed(2)}) and manually added payouts will be removed from the calculation. The manually added task clock-in data will be cleared.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={handleEditServiceCancel}>
            No
          </Button>
          <Button variant="contained" color="primary" onClick={handleEditServiceConfirm}>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
